import React, { ChangeEvent, useEffect, useState } from 'react';
import './App.css';
import Dice from './components/Dice';
import DisplayAds from './displayAd';

function App(): React.ReactElement {
  const [numDice, setNumDice] = useState<number>(1); // 주사위 개수
  const [diceValues, setDiceValues] = useState<number[]>(Array.from({ length: 1 }, () => 1)); // 주사위 값들
  const [threshold, setThreshold] = useState<number | null>(null); // 기준값
  const [rolling, setRolling] = useState<boolean>(false); // 주사위 굴림 애니메이션 상태

  useEffect(() => {
    // 초기 기준값 설정: (주사위 개수 * 6) / 2
    setThreshold(Math.floor((numDice * 6) / 2));
  }, [numDice]);

  const rollDice = (): void => {
    setRolling(true); // 애니메이션 시작
    setTimeout(() => {
      const newDiceValues: number[] = Array.from({ length: numDice }, () => Math.floor(Math.random() * 6) + 1);
      setDiceValues(newDiceValues);
      setRolling(false); // 애니메이션 종료
    }, 1000); // 1초 후에 값 변경 및 애니메이션 종료
  };

  // 통계 계산 함수들
  const calculateTotal = (): number => {
    return diceValues.reduce((sum, value) => sum + value, 0);
  };

  const calculateEvenOddCounts = (): { even: number; odd: number } => {
    let even: number = 0;
    let odd: number = 0;
    diceValues.forEach((value: number) => {
      if (value % 2 === 0) {
        even++;
      } else {
        odd++;
      }
    });
    return { even, odd };
  };

  const calculateSameNumberCounts = (): { [key: number]: number } => {
    const counts: { [key: number]: number } = {};
    for (let i = 1; i <= 6; i++) {
      counts[i] = 0;
    }
    diceValues.forEach((value: number) => {
      counts[value]++;
    });
    return counts;
  };

  const calculateThresholdCounts = (): { lessThan: number; greaterThanOrEqual: number } => {
    let lessThan: number = 0;
    let greaterThanOrEqual: number = 0;
    diceValues.forEach((value: number) => {
      if (threshold !== null) {
        if (value < threshold) {
          lessThan++;
        } else {
          greaterThanOrEqual++;
        }
      }
    });
    return { lessThan, greaterThanOrEqual };
  };

  const { even, odd } = calculateEvenOddCounts();
  const sameNumberCounts = calculateSameNumberCounts();
  const { lessThan, greaterThanOrEqual } = calculateThresholdCounts();

  return (
    <div className="min-h-screen bg-blue-100 flex flex-col items-center justify-center p-4 font-sans">
      <h1 className="text-5xl font-extrabold text-blue-800 mb-8 drop-shadow-lg">주사위 굴리기 게임</h1>

      <div className="bg-white p-6 rounded-xl shadow-lg mb-6 w-full max-w-md">
        <div className="mb-4">
          <label htmlFor="numDice" className="block text-lg font-medium text-gray-700 mb-1">주사위 개수:</label>
          <input
            type="number"
            id="numDice"
            min="1"
            value={numDice}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setNumDice(Number(e.target.value))}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="threshold" className="block text-lg font-medium text-gray-700 mb-1">기준값:</label>
          <input
            type="number"
            id="threshold"
            value={threshold !== null ? threshold : ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setThreshold(Number(e.target.value))}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
          />
        </div>

        <button
          onClick={rollDice}
          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg text-xl font-semibold shadow-md hover:from-blue-600 hover:to-purple-700 transition duration-300 transform hover:scale-105"
        >
          주사위 굴리기
        </button>
      </div>

      <div className="w-full flex flex-nowrap overflow-x-auto justify-center gap-6 mb-8">
        {diceValues.map((value: number, index: number) => (
          <Dice key={index} value={value} rolling={rolling} />
        ))}
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">통계</h2>
        <p className="text-xl text-gray-700 mb-2">총합: <span className="font-extrabold text-blue-600">{calculateTotal()}</span></p>
        <p className="text-xl text-gray-700 mb-2">홀수: <span className="font-extrabold text-purple-600">{odd}</span>개, 짝수: <span className="font-extrabold text-green-600">{even}</span>개</p>
        <div className="text-xl text-gray-700 mb-2">
          같은 수:
          {Object.entries(sameNumberCounts).map(([number, count]) => (
            <span key={number} className="ml-3"><span className="font-extrabold text-yellow-600">{Number(number)}</span>: {count}개</span>
          ))}
        </div>
        {threshold !== null && (
          <p className="text-xl text-gray-700">기준값({threshold}) 미만: <span className="font-extrabold text-blue-400">{lessThan}</span>개, 이상: <span className="font-extrabold text-blue-500">{greaterThanOrEqual}</span>개</p>
        )}
      </div>
      <div className="w-full flex justify-center my-8">
        <DisplayAds /><DisplayAds/>
      </div>
    </div>
  );
}

export default App;